import {
  applicabilityChoices,
  isBooleanApplicabilityField,
  normalizeApplicabilityValue,
  type GridApplicability,
  type GridApplicabilityField,
  type GridApplicabilityInput,
  type PricingRequest,
} from '@carrierbridge/shared';

/**
 * Valeurs de l'expédition que les grilles peuvent restreindre (périmètre
 * d'application). Une cotation manuelle ne les connaît pas d'office : sans
 * elles, seules les grilles sans restriction cotent — une grille « Sacs » ou
 * « ADR » n'apparaît donc jamais tant qu'on ne dit pas de quoi il s'agit.
 *
 * Chaîne vide = « indifférent » (champ non renseigné).
 */
export interface ShipmentCriteria {
  incoterm: string;
  typeOfGoods: string;
  typeOfFlow: string;
  transportMode: string;
  /** Code du moyen (SEMI_44…), pas son nom : c'est le code que les grilles restreignent. */
  means: string;
  dangerousGoods: TriState;
  temperatureControlled: TriState;
}

export type TriState = '' | 'true' | 'false';

export const EMPTY_SHIPMENT_CRITERIA: ShipmentCriteria = {
  incoterm: '',
  typeOfGoods: '',
  typeOfFlow: '',
  transportMode: '',
  means: '',
  dangerousGoods: '',
  temperatureControlled: '',
};

/** Ordre d'affichage : la marchandise d'abord, ce qui la précise ensuite. */
export const CRITERIA_FIELDS: readonly GridApplicabilityField[] = [
  'typeOfGoods',
  'typeOfFlow',
  'incoterm',
  'transportMode',
  'means',
  'dangerousGoods',
  'temperatureControlled',
];

/** Vrai dès qu'un critère est renseigné — l'expédition n'est plus « sans précision ». */
export function hasShipmentCriteria(criteria: ShipmentCriteria): boolean {
  return Object.values(criteria).some((v) => v !== '');
}

/** Part de la requête de cotation portée par les critères (rien si indifférent). */
export function criteriaToRequest(
  criteria: ShipmentCriteria,
): Pick<
  PricingRequest,
  | 'incoterm'
  | 'typeOfGoods'
  | 'typeOfFlow'
  | 'transportMode'
  | 'means'
  | 'dangerousGoods'
  | 'temperatureControlled'
> {
  return {
    incoterm: criteria.incoterm || undefined,
    typeOfGoods: criteria.typeOfGoods || undefined,
    typeOfFlow: criteria.typeOfFlow || undefined,
    transportMode: criteria.transportMode || undefined,
    means: criteria.means || undefined,
    dangerousGoods: triStateToBoolean(criteria.dangerousGoods),
    temperatureControlled: triStateToBoolean(criteria.temperatureControlled),
  };
}

function triStateToBoolean(value: TriState): boolean | undefined {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

/** Un critère tel que le formulaire doit le présenter. */
export interface CriterionView {
  field: GridApplicabilityField;
  /** Valeurs permises par les grilles qui peuvent coter, compte tenu des autres choix. */
  values: string[];
  /** « Indifférent » est un choix valable (une grille compatible n'est pas restreinte ici). */
  allowAny: boolean;
  /** Au moins une grille restreint ce critère : il y a quelque chose à choisir. */
  visible: boolean;
  /** Visible et sans « Indifférent » : sans valeur, aucune grille ne cote. */
  required: boolean;
}

/**
 * Les critères à proposer, dans l'ordre d'affichage, d'après les grilles qui
 * peuvent coter (`scopes`) et ce qui est déjà choisi.
 *
 * C'est une cascade, comme pays → région → ville : chaque critère n'est
 * restreint que par ceux affichés AVANT lui. Si tous se restreignaient entre
 * eux, choisir « sacs » imposerait l'incoterm EXW, qui à son tour ne laisserait
 * plus choisir que « sacs » — on ne pourrait plus revenir en arrière. Ainsi on
 * peut toujours rechanger un critère précédent ; les suivants s'adaptent.
 *
 * Un critère laissé vide est une décision (« indifférent ») quand elle est
 * permise : les grilles restreintes sur lui sortent alors du jeu pour les
 * suivants. Vide mais obligatoire, il n'est pas encore décidé et n'écarte rien.
 *
 * Un critère qu'aucune grille compatible ne restreint n'est pas `visible` : le
 * proposer ne débloquerait rien et noierait ceux qui comptent.
 */
export function criterionViews(
  scopes: readonly (GridApplicability | null | undefined)[],
  criteria: ShipmentCriteria,
): CriterionView[] {
  const decided: Partial<Record<GridApplicabilityField, string | boolean | null>> = {};
  return CRITERIA_FIELDS.map((field) => {
    const { values, allowAny } = applicabilityChoices(scopes, decided as GridApplicabilityInput, field);
    const visible = values.length > 0;
    const required = visible && !allowAny;

    const chosen = criteria[field];
    if (chosen !== '') decided[field] = isBooleanApplicabilityField(field) ? chosen === 'true' : chosen;
    else if (!required) decided[field] = null;

    return { field, values, allowAny, visible, required };
  });
}

function sameValue(a: string, b: string): boolean {
  return normalizeApplicabilityValue(a) === normalizeApplicabilityValue(b);
}

/**
 * Remet la sélection d'accord avec les choix permis.
 *
 * Choisir une valeur change ce que permettent les critères suivants : une
 * valeur qui n'est plus offerte est retirée, et un critère obligatoire qui n'a
 * qu'une seule issue est renseigné d'office. Une seule passe dans l'ordre
 * d'affichage suffit, puisque chaque critère ne dépend que des précédents.
 *
 * Sans aucune grille connue (chargement, erreur), la sélection est rendue
 * telle quelle : ne rien savoir n'autorise pas à effacer ce que l'on a saisi.
 * Rend l'objet reçu lui-même quand rien ne bouge.
 */
export function reconcileCriteria(
  scopes: readonly (GridApplicability | null | undefined)[],
  criteria: ShipmentCriteria,
): ShipmentCriteria {
  if (scopes.length === 0) return criteria;

  let current = criteria;
  for (const field of CRITERIA_FIELDS) {
    // Vue recalculée sur la sélection déjà ajustée : les critères précédents
    // viennent peut-être de changer ce que permet celui-ci.
    const view = criterionViews(scopes, current).find((v) => v.field === field)!;
    const chosen = current[field];
    let next = chosen;
    if (chosen !== '') next = view.values.find((v) => sameValue(v, chosen)) ?? '';
    if (next === '' && view.required && view.values.length === 1) next = view.values[0];
    if (next !== chosen) current = { ...current, [field]: next };
  }
  return current;
}
